<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
import { computed } from "vue";
import { ShieldCheck } from "@lucide/vue";

import { WizardProjectType } from "@/features/BuildingProject";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

import Switch from "@/components/Reusables/Switches/Switch.vue";
import InfoModalButton from "@/features/Tooltip/InfoModalButton.vue";
import StepIntro from "../StepIntro/StepIntro.vue";

const props = withDefaults(
  defineProps<{
    wasValidated: boolean;
    project: WizardProjectType;
    /**
     * Suppress this component's own StepIntro, for the merged Allowances step which renders one heading
     * above both halves. StepIntro is the screen's h1, so two of them is an accessibility defect as well as
     * a visual one. Additive and defaulted, so the edit wizard's separate step is untouched.
     */
    hideIntro?: boolean;
  }>(),
  { hideIntro: false }
);

const { field } = useWizardProject(() => props.project);

const specialConditionConfig = field("specialConditionConfig");

const isActive = computed<boolean>({
  get() {
    return !!specialConditionConfig.value?.enabled;
  },
  set(enabled: boolean) {
    specialConditionConfig.value = { enabled };
  },
});

/**
 * While the toggle is off this card has no body, so it has to end at the title bar. Kept as the shell's
 * bottom padding rather than anything on the band, because the band's negative margins cancel that
 * padding — leaving it in place fenced off a 20px empty strip that read as content failing to load.
 */
const shellClass = computed(() => (isActive.value ? "p-px-20" : "px-px-20 pt-px-20"));

/**
 * The band's rule divides the title bar from the body. With no body it is the card's own bottom border
 * drawn twice, so it goes and the band takes the shell's bottom corners instead (`rounded-b-8` rather
 * than `overflow-hidden`, matching the card grammar — see StepCard).
 */
const bandClass = computed(() => (isActive.value ? "border-b border-slate-300" : "rounded-b-8"));
</script>

<template>
  <div class="flex w-full flex-col">
    <StepIntro
      v-if="!hideIntro"
      class="mb-4"
      heading="Does the building have special conditions?"
      hint="Specific requirements in the Danish Building Regulations. If you are unsure whether this applies to your project, leave this as it is and continue to the next step." />

    <div :class="['rounded-8 bg-slate-0 flex flex-col gap-3 border border-slate-300', shellClass]">
      <div :class="['-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 bg-slate-50 pb-3', bandClass]">
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <ShieldCheck
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Special conditions</CardHeading>
            <!-- Rendered here rather than on StepIntro's action slot so it survives hideIntro: the merged
                 Allowances step (create wizard) suppresses this component's own StepIntro entirely, and would
                 take an action-slot affordance down with it. -->
            <InfoModalButton markdown-path="StandardValues" />
          </div>
          <Switch
            color="primary"
            :value="isActive"
            label="Use special conditions"
            @update:value="(value) => (isActive = value)" />
        </div>

        <p
          v-if="!isActive"
          class="t-caption text-type-muted">
          Off for most projects. Enable only if you can document that your building qualifies.
        </p>
      </div>

      <!-- The short "off" line lives in the title bar as this card's caption, so this block can no longer
           be its `v-else` — same condition, spelled out, so the two stay mutually exclusive. -->
      <div
        v-if="isActive"
        class="t-small flex flex-col gap-4">
        <strong>Special conditions are now activated in your project.</strong>
        <span
          >When the project is created and the building has been modeled, go to the material inventory to specify which
          constructions are affected by these conditions. This is done at the type level - open the type view and apply special
          conditions to the relevant types.</span
        >
        <span
          >If your building has special conditions, the regulations allow you to exclude the additional material impact caused by
          these conditions.
        </span>

        <!-- One guidance link, not two: this block used to end with a second sentence pointing at the
             same URL, which read as two different documents. -->
        <span>
          See the
          <a
            class="text-app-blue-600 font-semibold underline hover:no-underline"
            href="https://www.bygningsreglementet.dk/tekniske-bestemmelser/11/brv/bygningers-klimapaavirkning-1-juli-2025/"
            target="_blank"
            >official guidance</a
          >
          for examples of when special conditions may apply.
        </span>
        <span
          >Approval of special conditions is case-specific, so only enable this option if you can document that your building
          qualifies.</span
        >
      </div>
    </div>
  </div>
</template>
